import type {
	PortalStatementDetailResponseDto,
	PortalStatementDownloadRequestDto,
	PortalStatementIdsResponseDto,
	PortalStatementListResponseDto,
	PortalStatementQueryDto,
} from '@supply-mover-2-0/shared';
import {
	buildQueryString,
	portalFetch,
	portalFetchBlob,
} from '../lib/apiClient';

/**
 * Fetch the paginated statement list across the user's assigned clients.
 */
export async function getStatements(
	token: string,
	params: PortalStatementQueryDto,
): Promise<PortalStatementListResponseDto> {
	const qs = buildQueryString({
		page: params.page,
		limit: params.limit,
		clientId: params.clientId,
		dateFrom: params.dateFrom,
		dateTo: params.dateTo,
	});
	return portalFetch<PortalStatementListResponseDto>(`/statements${qs}`, token);
}

/**
 * Fetch a single statement with its rendered snapshot.
 */
export async function getStatementDetail(
	token: string,
	statementId: number,
): Promise<PortalStatementDetailResponseDto> {
	return portalFetch<PortalStatementDetailResponseDto>(
		`/statements/${statementId}`,
		token,
	);
}

/**
 * Download a statement PDF — rendered on the fly from the stored snapshot.
 */
export async function downloadStatementPdf(
	token: string,
	statementId: number,
): Promise<Blob> {
	return portalFetchBlob(`/statements/${statementId}/pdf`, token);
}

/**
 * PD-14241 — every statement id matching the filters, in list order.
 * Powers "Select all": the selection becomes an explicit id set, so
 * unchecking a few afterwards works across pages.
 */
export async function getStatementIds(
	token: string,
	params: Pick<PortalStatementQueryDto, 'clientId' | 'dateFrom' | 'dateTo'>,
): Promise<PortalStatementIdsResponseDto> {
	const qs = buildQueryString({
		clientId: params.clientId,
		dateFrom: params.dateFrom,
		dateTo: params.dateTo,
	});
	return portalFetch<PortalStatementIdsResponseDto>(
		`/statements/ids${qs}`,
		token,
	);
}

/**
 * PD-14241 — download statements as a single zip of PDFs, streamed by the
 * server as each PDF renders. `statementIds` carries the checkbox
 * selection; omitted = every statement matching the filters.
 */
export async function downloadStatementsZip(
	token: string,
	request: PortalStatementDownloadRequestDto,
): Promise<Blob> {
	return portalFetchBlob('/statements/download-all', token, {
		method: 'POST',
		body: request,
	});
}
